import { addMinutes } from 'date-fns';
import { Alert, Platform } from 'react-native';

import api from '../../../api/api';
import { StudyEventFormDataSourceProps } from '../../../screens/study/StudyEventFormDataSource';
import { DynamicFormContextState } from '../../DynamicFormContext';
import { ActionPlugin } from '../ActionPlugin';
import { log } from '../../../tools/LogHelper';

export interface AssignFormAction {
  dataSource?: string;
  value?: string;
  /**
   * ParticipantEvent name field
   */
  eventName?: string;
  /**
   * The form template to assign
   */
  formTemplateId?: number;
  /**
   * Determines if the notification time of the assigned form is calculated
   * based on a relative time or an absolute time.
   */
  notificationTimeType?: 'relative' | 'absolute' | 'inherit';
  /**
   * Determines if the relative notification time is scheduled based on when
   * action is triggered or based on a time field completed by the participant.
   */
  relativeType?: 'now' | 'dataSource';
  /**
   * The referenced entity data source column name for the time field used in
   * the notification time calculation.
   */
  relativeDataSource?: string;
  /**
   * The duration in minutes to add for the relative time notification of the
   * assigned form.
   */
  relativeDuration?: number;
  /**
   * If notification time type is 'absolute', then use this field for the
   * notification time of the assigned form.
   */
  absoluteTime?: string;
  /**
   * The Duration in days until the event expires
   */
  expireDayCount?: number | null;
}

export const AssignFormActionPlugin: ActionPlugin<AssignFormAction> = {
  type: 'AssignFormAction',
  validate: () => true,
  action: async (state, params) => {
    // console.log('AssignFormAction', params);
    if (!state.context?.studyParticipantId) {
      console.error('AssignFormAction: No studyParticipantId specified');
      return;
    }
    const context = state.context as DynamicFormContextState<
      any,
      any,
      StudyEventFormDataSourceProps
    >;
    // console.log('state.context', state.context);
    const { dataSourceContext, formValues } = context;
    if (!dataSourceContext?.participantEventId) {
      console.error(
        'AssignFormAction: dataSourceContext.participantEventId not provided.'
      );
      return;
    }
    const {
      participantEventId,
      occurrenceCount,
      intendedStartDate,
      expireDayCount: refEventExpireDayCount
    } = dataSourceContext;
    const {
      dataSource,
      value,
      formTemplateId,
      eventName,
      notificationTimeType,
      relativeType,
      relativeDataSource,
      relativeDuration,
      absoluteTime,
      expireDayCount
    } = params ?? {};
    let startDate = new Date();
    if (dataSource && String(formValues[dataSource]) !== String(value)) {
      log('AssignFormAction: did not find a match to assign a new form.');
      return;
    }
    // console.log('AssignFormAction: found value match.');
    if (notificationTimeType === 'relative' && relativeDuration) {
      if (relativeType === 'dataSource') {
        if (!relativeDataSource) {
          showAlert('AssignFormAction: relative data source not specified.');
          return;
        }
        // get the time value specified by the user to calculate the relative
        // notification time
        const time: string = formValues[relativeDataSource];
        if (!time) {
          showAlert(
            `AssignFormAction: relative time not specified in field '${relativeDataSource}'; cannot assign the new form.`
          );
          return;
        }
        const [hours, minutes] = time.split(':').map(i => +i);
        startDate.setHours(hours);
        startDate.setMinutes(minutes);
      }
      // calculate the notification time (either from now or from the user specified time)
      startDate = addMinutes(startDate, relativeDuration);
    } else if (notificationTimeType === 'absolute' && absoluteTime) {
      const [hours, minutes] = absoluteTime.split(':').map(i => +i);
      startDate.setHours(hours);
      startDate.setMinutes(minutes);
    } else if (notificationTimeType === 'inherit' && intendedStartDate) {
      startDate = intendedStartDate;
    }
    const result = await api.post(
      `study-participants/${state.context.studyParticipantId}/events`,
      {
        name: eventName,
        participantEventId,
        formTemplateId,
        startDate,
        participantAccessible: true,
        internalEditable: false,
        allowMultipleEntries: false,
        anyTimeDayOf: false,
        expireDayCount:
          notificationTimeType === 'inherit'
            ? refEventExpireDayCount || 3
            : expireDayCount,
        occurrenceCount: occurrenceCount
      }
    ).response;
    if (result.status === 409) {
      // Participant event already created
    }
  }
};

function showAlert(message: string) {
  if (Platform.OS === 'web') {
    alert(message);
  } else {
    Alert.alert('Error', message, [{ text: 'Ok' }]);
  }
}
